import { Button } from "@/components/ui/button";
import { Play, Video, Clock, Dumbbell, Zap } from "lucide-react";

interface WorkoutPreviewProps {
  routine: {
    name: string;
    exercises?: any[];
  };
  durationInMinutes: number;
  exerciseCount: number;
  onStartWorkout: () => void;
}

export const WorkoutPreview = ({
  routine,
  durationInMinutes,
  exerciseCount,
  onStartWorkout,
}: WorkoutPreviewProps) => {
  const hasVideo = routine.exercises?.some(exercise => exercise.video_url);

  return (
    <div className="flex flex-col items-center justify-center min-h-screen p-6 animate-fade-in">
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-primary/10 rounded-full blur-3xl" />
      </div>

      <div className="w-full max-w-md space-y-8 text-center relative z-10">
        {/* Main card with glassmorphism */}
        <div className="bg-white/10 dark:bg-white/5 backdrop-blur-xl border border-white/20 dark:border-white/10 rounded-3xl shadow-2xl p-6 space-y-6">
          {/* Icon header */}
          <div className="flex justify-center">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center shadow-lg shadow-primary/30">
              <Zap className="h-8 w-8 text-primary-foreground" />
            </div>
          </div>

          {/* Title */}
          <h1 className="text-2xl font-bold bg-gradient-to-r from-primary via-primary/80 to-primary/60 bg-clip-text text-transparent">
            {routine.name}
          </h1>
          
          {/* Stats grid */}
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 rounded-2xl bg-white/5 dark:bg-white/[0.03] backdrop-blur-sm border border-white/10 space-y-1.5">
              <div className="flex items-center justify-center">
                <Clock className="h-4 w-4 text-primary/80" />
              </div>
              <p className="text-muted-foreground text-xs uppercase tracking-wider">Duración</p>
              <p className="text-2xl font-bold text-foreground">{durationInMinutes}<span className="text-base text-muted-foreground ml-1">min</span></p>
            </div>
            <div className="p-3 rounded-2xl bg-white/5 dark:bg-white/[0.03] backdrop-blur-sm border border-white/10 space-y-1.5">
              <div className="flex items-center justify-center">
                <Dumbbell className="h-4 w-4 text-primary/80" />
              </div>
              <p className="text-muted-foreground text-xs uppercase tracking-wider">Ejercicios</p>
              <p className="text-2xl font-bold text-foreground">{exerciseCount}</p>
            </div>
          </div>

          {/* Video indicator */}
          {hasVideo && (
            <div className="flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl bg-primary/10 border border-primary/20">
              <Video className="h-4 w-4 text-primary" />
              <span className="text-sm text-foreground font-medium">Videos de ejercicios incluidos</span>
            </div>
          )}

          {/* Start button */}
          <Button
            onClick={onStartWorkout}
            className="w-full py-6 text-lg font-semibold bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 text-primary-foreground transition-all duration-300 rounded-2xl shadow-xl shadow-primary/25 hover:shadow-primary/40 hover:scale-[1.02] active:scale-[0.98]"
          >
            <Play className="mr-2 h-5 w-5" />
            Comenzar Entrenamiento
          </Button>
        </div>
      </div>
    </div>
  );
};
